import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, X, Lock } from "lucide-react";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import PortalPopup from "../../components/PortalPopup";
import ExportModalImage from "../../components/ExportModalImage";
import NavigationPanel from "../../components/NavigationPanel";
import Footer from "../../components/Footer";
import SettingNav from "../../components/settingNav";
import ToastMsg from "../../components/ToastMsg";
import TabState from "../../components/TabState";

const API_URL = process.env.REACT_APP_API_URL;

// Inline SVGs so Business Central and Keap don't need separate asset files
// dropped into /assets — swap for real asset files whenever convenient.
const BC_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="6" fill="#0F6CBD"/><path d="M8 9h16v3.2H8V9zm0 5.4h16v3.2H8v-3.2zm0 5.4h10.5v3.2H8V19.8z" fill="white"/></svg>`;
const BC_LOGO_DATA_URI = `data:image/svg+xml,${encodeURIComponent(BC_LOGO_SVG)}`;

const KEAP_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="6" fill="#EA580C"/><path d="M10 7h3.2v8.2L18.4 9h3.9l-5.9 6.9L22.6 25h-3.9l-4.4-6.9-1.1 1.3V25H10V7z" fill="white"/></svg>`;
const KEAP_LOGO_DATA_URI = `data:image/svg+xml,${encodeURIComponent(KEAP_LOGO_SVG)}`;

// Xero's official "Connect to Xero" button mark (light blue, white Xero
// roundel) — required by Xero's brand guidelines for the Connect button
// specifically. Swap for their downloaded PNG/SVG asset from
// https://developer.xero.com/documentation/xero-app-store/xero-connect-button/
// if you want the pixel-exact official asset instead of this inline version.
const XERO_CONNECT_MARK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#ffffff"/><path d="M12 10.6 8.6 7.2a1 1 0 1 0-1.4 1.4L10.6 12l-3.4 3.4a1 1 0 1 0 1.4 1.4L12 13.4l3.4 3.4a1 1 0 1 0 1.4-1.4L13.4 12l3.4-3.4a1 1 0 1 0-1.4-1.4L12 10.6z" fill="#13B5EA"/></svg>`;
const XERO_CONNECT_MARK_DATA_URI = `data:image/svg+xml,${encodeURIComponent(XERO_CONNECT_MARK_SVG)}`;

// Add/remove providers here. `id` must match the backend route prefix
// (e.g. /api/xero/*, /api/quickbooks/*, /api/keap/*).
const INTEGRATIONS = [
  { id: "xero", name: "Xero", logo: "assets/xero.svg" },
  { id: "quickbooks", name: "QuickBooks", logo: "assets/quickbooks-seeklogo.png" },
  { id: "sage",        name: "Sage",        logo: "assets/sage.svg" },
  { id: "business-central", name: "Business Central", logo: BC_LOGO_DATA_URI },
  { id: "keap", name: "Keap", logo: KEAP_LOGO_DATA_URI },
];

const defaultState = () =>
  INTEGRATIONS.reduce((acc, { id }) => {
    acc[id] = {
      connected: false,
      syncing: false,
      connecting: false,
      invoiceCount: 0,
      lastSync: null,
      accessMessage: "",
      // Message from a failed /connect call, e.g. the backend's
      // { success: false, message: "Only company owners can connect Keap" }.
      connectError: "",
    };
    return acc;
  }, {});

const KEAP_ERROR_MESSAGES = {
  account_in_use:
    "That Keap account is already connected to another company. Sign out of Keap (or use a private window), then connect with this company's Keap account.",
  invalid_state:
    "The Keap connection request expired or didn't match your account. Please try connecting again.",
};

const SettingsAccount2Page = () => {
  const navigate = useNavigate();
  const [isToastMsgOpen, setToastMsgOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isExportModalImageOpen, setExportModalImageOpen] = useState(false);

  // Integration states, keyed by provider id
  const [integrations, setIntegrations] = useState(defaultState);
const [isCompanyOwner, setIsCompanyOwner] = useState(false);

// ── Business Central environment/company picker ──
// BC's OAuth callback can't finish in one redirect like the others (Xero/QB/
// Sage/Keap just need a realm/tenant id or, for Keap, nothing extra at all).
// It needs an environment name typed in, then a company chosen from that
// environment, before it counts as "connected" — so it gets its own small
// picker flow layered on top of the generic connect/sync/disconnect
// handlers below.
const [isBcPickerOpen, setBcPickerOpen] = useState(false);
const [bcPickerStep, setBcPickerStep] = useState("environment"); // "environment" | "company"
const [bcEnvironmentInput, setBcEnvironmentInput] = useState("Production");
const [bcCompanies, setBcCompanies] = useState([]);
const [bcSelectedCompanyId, setBcSelectedCompanyId] = useState("");
const [bcPickerLoading, setBcPickerLoading] = useState(false);
const [bcPickerError, setBcPickerError] = useState("");

const updateIntegration = (id, patch) =>
  setIntegrations((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

const getAuthToken = () => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    console.error("No token found in localStorage");
    alert("Please log in again");
    return null;
  }
  return token;
};

// Define checkConnectionStatus BEFORE the useEffects that reference it
const checkConnectionStatus = useCallback(async () => {
  const token = getAuthToken();
  if (!token) return;

  try {
    const userResponse = await fetch(`${API_URL}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const userData = await userResponse.json();
    if (userData.success) {
      setIsCompanyOwner(userData.data.isCompanyOwner || false);
    }
  } catch (error) {
    console.error("Error fetching user:", error);
  }

  await Promise.all(
    INTEGRATIONS.map(async ({ id }) => {
      try {
        const response = await fetch(`${API_URL}/api/${id}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success) {
          updateIntegration(id, {
            connected: data.connected,
            accessMessage: data.message || "",
          });
        }
      } catch (error) {
        console.error(`Error checking ${id} status:`, error);
      }
    }),
  );
}, []); // eslint-disable-line react-hooks/exhaustive-deps

// 1) Always check status on initial mount
useEffect(() => {
  checkConnectionStatus();
}, [checkConnectionStatus]);

// 2) Re-check after returning from OAuth (Xero, QuickBooks, Sage, Keap)
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  if (
    params.get("quickbooks") === "connected" ||
    params.get("xero") === "connected" ||
    params.get("sage")       === "connected" ||
    params.get("keap")       === "connected"
  ) {
    checkConnectionStatus();
    window.history.replaceState({}, "", window.location.pathname);
  }
}, [checkConnectionStatus]);

// 3) Business Central's OAuth callback lands here mid-flow — it still needs
// an environment name and a company chosen before it's actually connected.
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const bcParam = params.get("bc");

  if (bcParam === "pick-environment") {
    window.history.replaceState({}, "", window.location.pathname);
    setBcPickerError("");
    setBcPickerStep("environment");
    setBcPickerOpen(true);
  } else if (bcParam === "error") {
    window.history.replaceState({}, "", window.location.pathname);
    const reason = params.get("reason");
    alert(
      reason === "no_tenant"
        ? "Business Central sign-in didn't return tenant information. Please try again."
        : "Failed to connect Business Central. Please try again."
    );
  }
}, []); // eslint-disable-line react-hooks/exhaustive-deps

// 4) Keap's OAuth callback redirects straight back here on error too —
// show the specific reason inline on the Keap row.
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("keap") === "error") {
    window.history.replaceState({}, "", window.location.pathname);
    const reason = params.get("reason");
    updateIntegration("keap", {
      connected: false,
      accessMessage:
        KEAP_ERROR_MESSAGES[reason] || "Failed to connect Keap. Please try again.",
    });
  }
}, []); // eslint-disable-line react-hooks/exhaustive-deps



  // Redirects to the provider when the backend returns a URL; otherwise shows
  // the backend's own message inline on that provider's row (instead of a
  // generic alert that hid the real reason).
  const handleConnect = async ({ id, name }) => {
    const token = getAuthToken();
    if (!token) return;
    updateIntegration(id, { connecting: true, connectError: "" });
    try {
      const response = await fetch(`${API_URL}/api/${id}/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => null);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      updateIntegration(id, {
        connectError:
          data?.message || `We couldn't start the ${name} connection. Please try again.`,
      });
    } catch (error) {
      console.error(`Error connecting to ${name}:`, error);
      updateIntegration(id, {
        connectError: `We couldn't reach the server to connect ${name}. Please check your connection and try again.`,
      });
    } finally {
      updateIntegration(id, { connecting: false });
    }
  };

  // A sync that fails because the token/refresh-token is dead (expired,
  // revoked from the provider's side, etc.) flips the row back to
  // "Not connected" with a clear reconnect message.
  const handleSync = async ({ id, name }) => {
    updateIntegration(id, { syncing: true });
    const token = getAuthToken();
    if (!token) {
      updateIntegration(id, { syncing: false });
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/${id}/sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.success) {
        updateIntegration(id, {
          invoiceCount: data.count,
          lastSync: new Date(),
        });
        alert(`Successfully synced ${data.count} invoices from ${name}`);
      } else if (data.requiresReauth) {
        updateIntegration(id, {
          connected: false,
          lastSync: null,
          accessMessage: `${name} authorisation has expired. Please reconnect.`,
        });
      } else {
        alert(data.message || `Failed to sync ${name} invoices`);
      }
    } catch (error) {
      console.error(`Error syncing ${name} invoices:`, error);
      alert(`Failed to sync ${name} invoices`);
    } finally {
      updateIntegration(id, { syncing: false });
    }
  };

  const handleDisconnect = async ({ id, name }) => {
    if (!window.confirm(`Are you sure you want to disconnect ${name}?`)) return;
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/${id}/disconnect`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.success) {
        updateIntegration(id, { connected: false, lastSync: null, accessMessage: "", connectError: "" });
        alert(`${name} disconnected successfully`);
      } else {
        alert(data.message || `Failed to disconnect ${name}`);
      }
    } catch (error) {
      console.error(`Error disconnecting ${name}:`, error);
      alert(`Failed to disconnect ${name}`);
    }
  };

  const closeBcPicker = () => {
    setBcPickerOpen(false);
    setBcCompanies([]);
    setBcSelectedCompanyId("");
    setBcPickerError("");
  };

  const handleBcEnvironmentSubmit = async () => {
    if (!bcEnvironmentInput.trim()) {
      setBcPickerError("Enter an environment name (e.g. Production or Sandbox).");
      return;
    }
    const token = getAuthToken();
    if (!token) return;
    setBcPickerLoading(true); setBcPickerError("");
    try {
      const res  = await fetch(
        `${API_URL}/api/business-central/companies?environment=${encodeURIComponent(bcEnvironmentInput.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to list Business Central companies");
      if (!data.companies?.length) {
        setBcPickerError(`No companies found in the "${bcEnvironmentInput.trim()}" environment.`);
        return;
      }
      setBcCompanies(data.companies);
      setBcPickerStep("company");
    } catch (err) {
      console.error("BC list companies error:", err);
      setBcPickerError(err.message || "Failed to list companies for that environment.");
    } finally {
      setBcPickerLoading(false);
    }
  };

  const handleBcCompanySelect = async () => {
    if (!bcSelectedCompanyId) {
      setBcPickerError("Select a company to continue.");
      return;
    }
    const token = getAuthToken();
    if (!token) return;
    const chosen = bcCompanies.find((c) => c.id === bcSelectedCompanyId);
    setBcPickerLoading(true); setBcPickerError("");
    try {
      const res  = await fetch(`${API_URL}/api/business-central/select-company`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ companyId: bcSelectedCompanyId, companyName: chosen?.displayName || chosen?.name }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to save company selection");
      await checkConnectionStatus(); // refreshes integrations["business-central"].connected
      closeBcPicker();
    } catch (err) {
      console.error("BC select company error:", err);
      setBcPickerError(err.message || "Failed to save company selection.");
    } finally {
      setBcPickerLoading(false);
    }
  };

  const closeLogoutConfirmation = useCallback(
    () => setLogoutConfirmationOpen(false),
    [],
  );
  const openExportModalImage = useCallback(
    () => setExportModalImageOpen(true),
    [],
  );
  const closeExportModalImage = useCallback(
    () => setExportModalImageOpen(false),
    [],
  );
  const openToastMsg = useCallback(() => setToastMsgOpen(true), []);
  const closeToastMsg = useCallback(() => setToastMsgOpen(false), []);

  const tabs = [
    { label: "Profile", to: "/setting-account" },
    { label: "Connected accounts", to: "/setting-account2" },
    { label: "Security", to: "/setting-account3" },
    { label: "Delete account", to: "/setting-account4" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
        html, body { background: rgb(241, 245, 249) !important; }
        .dd-root * { box-sizing: border-box; }
        .dd-root { font-family: 'DM Sans', sans-serif; background: rgb(241, 245, 249) !important; min-height: 100vh; }
        .dd-card { background: #ffffff; border: 1px solid #E2E8F0; border-radius: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04); transition: box-shadow 0.2s ease; position: relative; overflow: hidden; }
        .dd-section-divider { border: none; border-top: 1px solid #E2E8F0; margin: 0; }
        .dd-section-label { font-size: 14px; font-weight: 700; color: #0F172A; margin: 0 0 12px 0; }
        .dd-section-sublabel { font-size: 13px; color: #64748B; margin: 2px 0 0 0; }
        .dd-row-divider { border: none; border-top: 1px solid #F1F5F9; margin: 0; }
        .dd-btn-secondary { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A; cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05); transition: border-color 0.15s, background 0.15s; }
        .dd-btn-secondary:hover { border-color: #94A3B8; background: #F8FAFC; }
        .dd-btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }
        .dd-btn-primary { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; background: #2563EB; border: 1px solid #2563EB; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #ffffff; cursor: pointer; transition: background 0.15s, box-shadow 0.15s; }
        .dd-btn-primary:hover { background: #1D4ED8; box-shadow: 0 4px 12px rgba(37,99,235,0.25); }
        .dd-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .dd-btn-danger { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; background: #EF4444; border: 1px solid #EF4444; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #ffffff; cursor: pointer; transition: background 0.15s, box-shadow 0.15s; }
        .dd-btn-danger:hover { background: #DC2626; box-shadow: 0 4px 12px rgba(239,68,68,0.25); }
        .dd-btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
        .dd-btn-xero-connect { display: inline-flex; align-items: center; gap: 8px; padding: 8px 18px 8px 10px; background: #13B5EA; border: 1px solid #13B5EA; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700; color: #ffffff; cursor: pointer; transition: background 0.15s, box-shadow 0.15s; }
        .dd-btn-xero-connect:hover { background: #0FA3D6; box-shadow: 0 4px 12px rgba(19,181,234,0.3); }
        .dd-btn-xero-connect:disabled { opacity: 0.6; cursor: not-allowed; }

        .dd-owner-only { display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 12.5px; font-weight: 600; color: #64748B; white-space: nowrap; }

        /* ── Inline connect-error alert (backend message from /connect) ── */
        @keyframes dd-connect-error-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .dd-connect-error { display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; margin-bottom: 12px; background: #FEF2F2; border: 1px solid #FECACA; border-radius: 12px; animation: dd-connect-error-in 0.2s ease-out; }
        .dd-connect-error-title { font-size: 13px; font-weight: 700; color: #991B1B; line-height: 1.3; }
        .dd-connect-error-message { font-size: 13px; color: #B91C1C; line-height: 1.5; margin-top: 2px; word-break: break-word; }
        .dd-connect-error-close { width: 24px; height: 24px; border-radius: 6px; border: none; background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: background 0.15s; }
        .dd-connect-error-close:hover { background: #FEE2E2; }
        @media (prefers-reduced-motion: reduce) { .dd-connect-error { animation: none; } }
      `}</style>

      <div
        className="dd-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 24 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              Settings
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
              Get your billing information, generate/review invoices.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 32 }}>
            <SettingNav />

            <div
              className="dd-card"
              style={{
                flex: 1,
                padding: 28,
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  paddingBottom: 20,
                  borderBottom: "1px solid #E2E8F0",
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                  >
                    Account
                  </div>
                  <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                    Allow users to configure how and when communication is
                    initiated with debtors.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <button className="dd-btn-secondary">Cancel</button>
                  <button className="dd-btn-primary" onClick={openToastMsg}>
                    Save
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: -8 }}>
                {tabs.map((tab) => (
                  <TabState key={tab.to} label={tab.label} to={tab.to} />
                ))}
              </div>

              <hr className="dd-section-divider" />

              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <p className="dd-section-label" style={{ margin: 0 }}>
                  Connected accounts
                </p>

                {INTEGRATIONS.map((integration, index) => {
                  const { id, name, logo } = integration;
                  const state = integrations[id];
                  const isXero = id === "xero";
                  return (
                    <div key={id}>
                      {index > 0 && (
                        <hr
                          className="dd-row-divider"
                          style={{ marginBottom: 16 }}
                        />
                      )}

                      {state.connectError && (
                        <div className="dd-connect-error" role="alert">
                          <AlertCircle
                            size={16}
                            style={{ color: "#DC2626", flexShrink: 0, marginTop: 1 }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="dd-connect-error-title">
                              Couldn't connect {name}
                            </div>
                            <div className="dd-connect-error-message">
                              {state.connectError}
                            </div>
                          </div>
                          <button
                            className="dd-connect-error-close"
                            onClick={() => updateIntegration(id, { connectError: "" })}
                            aria-label="Dismiss"
                          >
                            <X size={14} style={{ color: "#991B1B" }} />
                          </button>
                        </div>
                      )}

                      {state.accessMessage && (
                        <div
                          style={{
                            fontSize: 13,
                            color: state.connected ? "#2563EB" : "#EA580C",
                            marginBottom: 12,
                          }}
                        >
                          {state.accessMessage}
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 8,
                              border: "1px solid #E2E8F0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#F8FAFC",
                            }}
                          >
                            <img src={logo} style={{ width: 30 }} alt={name} />
                          </div>
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 14,
                                color: "#0F172A",
                              }}
                            >
                              {name}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: "#64748B",
                                marginTop: 2,
                              }}
                            >
                              {state.connected ? (
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      background: "#22C55E",
                                      display: "inline-block",
                                    }}
                                  />
                                  Connected
                                  {state.lastSync && (
                                    <span style={{ marginLeft: 8 }}>
                                      (Last synced:{" "}
                                      {state.lastSync.toLocaleString()})
                                    </span>
                                  )}
                                </span>
                              ) : (
                                "Not connected"
                              )}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          {state.connected ? (
                            <>
                              <button
                                className="dd-btn-secondary"
                                onClick={() => handleSync(integration)}
                                disabled={state.syncing}
                              >
                                {state.syncing ? "Syncing…" : "Sync Now"}
                              </button>
                              {isCompanyOwner && (
                                <button
                                  className="dd-btn-danger"
                                  onClick={() => handleDisconnect(integration)}
                                >
                                  Disconnect
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#2563EB",
                                  cursor: "default",
                                }}
                              >
                               
                              </span>
                              {isCompanyOwner ? (
                                // Xero gets its own branded "Connect to Xero" button
                                // per Xero's App Store brand guidelines — every other
                                // integration keeps the generic dd-btn-primary.
                                isXero ? (
                                  <button
                                    className="dd-btn-xero-connect"
                                    onClick={() => handleConnect(integration)}
                                    disabled={state.connecting}
                                  >
                                    <img
                                      src={XERO_CONNECT_MARK_DATA_URI}
                                      alt=""
                                      style={{ width: 18, height: 18 }}
                                    />
                                    {state.connecting ? "Connecting…" : "Connect to Xero"}
                                  </button>
                                ) : (
                                  <button
                                    className="dd-btn-primary"
                                    onClick={() => handleConnect(integration)}
                                    disabled={state.connecting}
                                  >
                                    {state.connecting ? "Connecting…" : "Connect"}
                                  </button>
                                )
                              ) : (
                                <span className="dd-owner-only" title={`Ask your company owner to connect ${name}`}>
                                  <Lock size={13} style={{ flexShrink: 0 }} />
                                  Only the company owner can connect {name}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isExportModalImageOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeExportModalImage}
        >
          <ExportModalImage onClose={closeExportModalImage} />
        </PortalPopup>
      )}
      {isBcPickerOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeBcPicker}
        >
          <div className="w-[440px] bg-white rounded-lg shadow-lg p-6">
            <div className="flex flex-col gap-4">
              <div className="text-xl font-semibold text-neutrals-neutral-900">
                {bcPickerStep === "environment" ? "Connect Business Central" : "Select a company"}
              </div>

              {bcPickerStep === "environment" && (
                <>
                  <div className="text-sm text-neutrals-neutral-300">
                    Enter the Business Central environment name. This is usually{" "}
                    <strong>Production</strong>, or <strong>Sandbox</strong> if you're using a trial or test environment.
                  </div>
                  <input
                    type="text"
                    value={bcEnvironmentInput}
                    onChange={(e) => setBcEnvironmentInput(e.target.value)}
                    placeholder="Production"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1px solid #E2E8F0",
                      borderRadius: 10,
                      fontFamily: "DM Sans, sans-serif",
                      fontSize: 14,
                      color: "#0F172A",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {bcPickerError && (
                    <div style={{ color: "#DC2626", fontSize: 13 }}>{bcPickerError}</div>
                  )}
                  <div className="flex gap-3 mt-2">
                    <button className="dd-btn-secondary" onClick={closeBcPicker}>Cancel</button>
                    <button
                      className="dd-btn-primary"
                      onClick={handleBcEnvironmentSubmit}
                      disabled={bcPickerLoading}
                    >
                      {bcPickerLoading ? "Looking up companies..." : "Continue"}
                    </button>
                  </div>
                </>
              )}

              {bcPickerStep === "company" && (
                <>
                  <div className="text-sm text-neutrals-neutral-300">
                    Choose which Business Central company to sync invoices from.
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 240, overflowY: "auto" }}>
                    {bcCompanies.map((c) => (
                      <label
                        key={c.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 14px",
                          border: `1px solid ${bcSelectedCompanyId === c.id ? "#0F6CBD" : "#E2E8F0"}`,
                          borderRadius: 10,
                          cursor: "pointer",
                          background: bcSelectedCompanyId === c.id ? "#F5F9FF" : "#fff",
                        }}
                      >
                        <input
                          type="radio"
                          name="bc-company"
                          value={c.id}
                          checked={bcSelectedCompanyId === c.id}
                          onChange={() => setBcSelectedCompanyId(c.id)}
                        />
                        <span style={{ fontSize: 14, color: "#0F172A", fontWeight: 600 }}>
                          {c.displayName || c.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {bcPickerError && (
                    <div style={{ color: "#DC2626", fontSize: 13 }}>{bcPickerError}</div>
                  )}
                  <div className="flex gap-3 mt-2">
                    <button className="dd-btn-secondary" onClick={() => setBcPickerStep("environment")}>Back</button>
                    <button
                      className="dd-btn-primary"
                      onClick={handleBcCompanySelect}
                      disabled={bcPickerLoading || !bcSelectedCompanyId}
                    >
                      {bcPickerLoading ? "Saving..." : "Connect"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </PortalPopup>
      )}

      {isToastMsgOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeToastMsg}
        >
          <ToastMsg onClose={closeToastMsg} />
        </PortalPopup>
      )}
    </>
  );
};

export default SettingsAccount2Page;